import { forwardRef, useState, useRef, useEffect } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import './CustomSelect.css';

const CustomSelect = ({ options, placeholder, onSelect, value, defaultValue, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Create a map of abbreviations to full options for quick lookup
  const abbreviationMap = options.reduce((map, option) => {
    const abbreviation = option.label.split(' ')[0];
    map[abbreviation.toUpperCase()] = option;
    return map;
  }, {});

  useEffect(() => {
    if (value) {
      const option = options.find(option => option.value === (value.value || value));
      setSelectedOption(option);
      setSearchTerm(option ? option.label : '');
    } else if (defaultValue) {
      const option = options.find(option => option.value === defaultValue);
      setSelectedOption(option);
      setSearchTerm(option ? option.label : '');
    }
  }, [value, options, defaultValue]);

  const filteredOptions = searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleSelect = (option) => {
    if (!disabled) {
      setSelectedOption(option);
      setSearchTerm(option.label);
      onSelect(option.value);
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    if (!disabled) {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      // US case
      if (abbreviationMap[newValue.toUpperCase()]) {
        if (e.nativeEvent.constructor.name === 'Event') {
          handleSelect(abbreviationMap[newValue.toUpperCase()]);
        } else {
          setIsOpen(true);
        }
      } 
      // Canada case
      else {
        const matchedOption = options.find(option => option.label.toLowerCase() === newValue.toLowerCase() && option.country === '38');
        if (matchedOption) {
          if (e.nativeEvent.constructor.name === 'Event') {
            handleSelect(matchedOption);
          } else {
            setIsOpen(true);
          }
        } else {
          setIsOpen(true);
        }
      }
    }
  };

  const handleToggle = (isOpenEvent) => {
    if (!disabled) {
      setIsOpen(isOpenEvent);
      if (isOpenEvent) {
        setSearchTerm('');
      } else {
        resetToSelectedOption();
      }
    }
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsFocused(true);
      setSearchTerm('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!isOpen) {
      resetToSelectedOption();
    }
  };

  const resetToSelectedOption = () => {
    setSearchTerm(selectedOption ? selectedOption.label : '');
  };

  return (
    <div
      className={`custom-select-wrapper ${isFocused ? 'focus' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <Dropdown 
        show={isOpen} 
        onToggle={handleToggle}
        drop="down"
        align="start"
      >
        <Dropdown.Toggle as={CustomToggle} disabled={disabled}>
          <Form.Control
            ref={inputRef}
            type="text"
            value={searchTerm}
            disabled={disabled}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={selectedOption ? selectedOption.label : placeholder}
            className={`custom-select-input ${selectedOption && !searchTerm ? 'selected' : ''}`}
          />
        </Dropdown.Toggle>
        <Dropdown.Menu className="custom-select-menu">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <Dropdown.Item 
                key={option.value} 
                onClick={() => handleSelect(option)}
                disabled={disabled}
              >
                {option.label}
              </Dropdown.Item>
            ))
          ) : (
            <Dropdown.Item disabled>No results</Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const CustomToggle = forwardRef(({ children, onClick, disabled }, ref) => (
  <button
    className={`custom-select-toggle ${disabled ? 'disabled' : ''}`}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      if (!disabled) {
        onClick(e);
      }
    }}
    disabled={disabled}
    aria-haspopup="listbox"
    style={{
      background: 'inherit',
    }}
  >
    {children}
    <span className="custom-select-arrow" aria-hidden="true"></span>
  </button>
));

export default CustomSelect;

